import { countryName } from '@crm/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, Download, Plus, Upload } from 'lucide-react'
import { useState } from 'react'
import { Button, buttonVariants } from '@/components/ui/button'
import { Badge, Card, Skeleton } from '@/components/ui/field'
import { Checkbox, Select } from '@/components/ui/select'
import {
  clientsCountQuery,
  clientsQuery,
  exportUrl,
  type ClientListItem,
} from '@/features/clients/api'
import { FilterBar } from '@/features/clients/filter-bar'
import { statusLabel, statusTone } from '@/features/clients/options'
import { clientSearchSchema, filterOnly, type ClientSearch } from '@/features/clients/search-schema'
import { BulkBar } from '@/features/clients/bulk-bar'
import { formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../../_app'

export const Route = createFileRoute('/_app/clients/')({
  validateSearch: clientSearchSchema,
  component: ClientListPage,
})

const DEFAULT_LIMIT = 25

function ClientListPage() {
  const { me } = AppRoute.useRouteContext()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const [selected, setSelected] = useState<Set<string>>(new Set())
  /** True when the user escalated from "this page" to "everything matching". */
  const [selectAllMatching, setSelectAllMatching] = useState(false)
  /** Cursors we've walked, so "Previous" can step back through keyset pages. */
  const [cursorStack, setCursorStack] = useState<string[]>([])

  const limit = search.limit ?? DEFAULT_LIMIT
  const filter = filterOnly(search)

  const list = useQuery(clientsQuery({ ...filter, limit, cursor: search.cursor }))
  const count = useQuery(clientsCountQuery(filter))

  const items = list.data?.items ?? []
  const total = count.data?.total ?? 0

  /**
   * Any filter change resets paging and selection: a cursor from the old result
   * set is meaningless against a new one, and silently keeping ticks for rows
   * that no longer match is how the wrong people get bulk-updated.
   */
  function patchSearch(patch: Partial<ClientSearch>) {
    setSelected(new Set())
    setSelectAllMatching(false)
    setCursorStack([])
    void navigate({ search: (prev) => ({ ...prev, ...patch, cursor: undefined }) })
  }

  function nextPage() {
    if (!list.data?.nextCursor) return
    setCursorStack((stack) => [...stack, search.cursor ?? ''])
    setSelected(new Set())
    setSelectAllMatching(false)
    void navigate({ search: (prev) => ({ ...prev, cursor: list.data!.nextCursor! }) })
  }

  function prevPage() {
    const previous = cursorStack[cursorStack.length - 1]
    setCursorStack((stack) => stack.slice(0, -1))
    setSelected(new Set())
    setSelectAllMatching(false)
    void navigate({ search: (prev) => ({ ...prev, cursor: previous || undefined }) })
  }

  const allOnPageSelected = items.length > 0 && items.every((i) => selected.has(i.id))

  function toggleAllOnPage() {
    setSelectAllMatching(false)
    setSelected(allOnPageSelected ? new Set() : new Set(items.map((i) => i.id)))
  }

  function toggleOne(id: string) {
    setSelectAllMatching(false)
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Clients</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {count.isLoading ? 'Counting…' : `${total.toLocaleString()} matching`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(exportUrl(filter), '_blank')}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
          {me.permissions.includes('clients:create') && (
            <Link
              to="/clients/import"
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              <Upload className="h-4 w-4" />
              Import
            </Link>
          )}
          {me.permissions.includes('clients:create') && (
            <Link to="/clients/new" className={buttonVariants()}>
              <Plus className="h-4 w-4" />
              Add client
            </Link>
          )}
        </div>
      </div>

      <Card className="p-4">
        <FilterBar search={search} onChange={patchSearch} />
      </Card>

      {(selected.size > 0 || selectAllMatching) && (
        <BulkBar
          selectedIds={[...selected]}
          selectAllMatching={selectAllMatching}
          matchingTotal={total}
          pageSize={items.length}
          filter={filter}
          onSelectAllMatching={() => setSelectAllMatching(true)}
          onClear={() => {
            setSelected(new Set())
            setSelectAllMatching(false)
          }}
        />
      )}

      <Card className="overflow-hidden">
        {list.isLoading && (
          <div className="space-y-2 p-6">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        )}
        {list.isError && (
          <p className="p-8 text-center text-sm text-[var(--color-destructive)]">
            Couldn&rsquo;t load clients.
          </p>
        )}
        {list.data && items.length === 0 && (
          <div className="p-10 text-center">
            <p className="text-sm font-medium">No clients match</p>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Try clearing a filter, or add a client.
            </p>
          </div>
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/50 text-left">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <Checkbox
                      checked={allOnPageSelected}
                      onChange={toggleAllOnPage}
                      aria-label="Select all on this page"
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Phone</th>
                  <th className="px-3 py-2 font-medium">NIC</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Countries</th>
                  <th className="px-3 py-2 font-medium">District</th>
                  <th className="px-3 py-2 font-medium">Assigned</th>
                  <th className="px-3 py-2 font-medium">Added</th>
                </tr>
              </thead>
              <tbody>
                {items.map((client) => (
                  <ClientRow
                    key={client.id}
                    client={client}
                    selected={selected.has(client.id)}
                    onToggle={() => toggleOne(client.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="page-size" className="text-[var(--color-muted-foreground)]">
            Per page
          </label>
          <Select
            id="page-size"
            className="h-8 w-20"
            value={String(limit)}
            onChange={(e) => patchSearch({ limit: Number(e.target.value) })}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={cursorStack.length === 0}
            onClick={prevPage}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button variant="outline" size="sm" disabled={!list.data?.nextCursor} onClick={nextPage}>
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function ClientRow({
  client,
  selected,
  onToggle,
}: {
  client: ClientListItem
  selected: boolean
  onToggle: () => void
}) {
  return (
    <tr className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-muted)]/30">
      <td className="px-3 py-2">
        <Checkbox checked={selected} onChange={onToggle} aria-label={`Select ${client.fullName}`} />
      </td>
      <td className="px-3 py-2">
        <Link
          to="/clients/$clientId"
          params={{ clientId: client.id }}
          className="font-medium text-[var(--color-primary)] hover:underline"
        >
          {client.fullName}
        </Link>
        {client.source === 'self_registration' && (
          <span className="ml-2 text-xs text-[var(--color-muted-foreground)]">self-registered</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2">{client.phone}</td>
      {/* Masked server-side — the full NIC is never sent to a list request. */}
      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-[var(--color-muted-foreground)]">
        {client.nicMasked ?? '—'}
      </td>
      <td className="px-3 py-2">
        <Badge variant={statusTone(client.status)}>{statusLabel(client.status)}</Badge>
      </td>
      <td className="px-3 py-2">
        {client.countries.length === 0 ? (
          <span className="text-[var(--color-muted-foreground)]">—</span>
        ) : (
          <span className="text-xs">
            {client.countries.slice(0, 2).map(countryName).join(', ')}
            {client.countries.length > 2 && ` +${client.countries.length - 2}`}
          </span>
        )}
      </td>
      <td className="px-3 py-2">{client.district ?? '—'}</td>
      <td className="px-3 py-2 text-[var(--color-muted-foreground)]">
        {client.assignedUserName ?? 'Unassigned'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
        {formatDateTime(client.createdAt)}
      </td>
    </tr>
  )
}
